import { APPURL } from "../../environments/environment";
import { Token } from "../after-login/token/token";
import { TokenService } from "./TokenService";

/**
 * LoginService - Handles login/logout navigation
 */
export class LoginService {
  private navigateFunc: ((path: string) => void) | null = null;
  private tokenService: TokenService;

  constructor(tokenService?: TokenService) {
    this.tokenService = tokenService || new TokenService();
  }

  /**
   * Set the navigate function from React Router
   * Call this in components that need programmatic navigation
   */
  setNavigate(navigate: (path: string) => void): void {
    this.navigateFunc = navigate;
  }

  /**
   * Navigate to the authentication page.
   * Note: tokens are NOT cleared before redirect — they persist during navigation.
   */
  goToLoginUrl(): void {
    if (this.navigateFunc) {
      this.navigateFunc("/authentication");
    } else {
      // Fallback to window.location for cases where navigate isn't set
      window.location.href = "/authentication";
    }
  }

  /**
   * Navigate to the logout page.
   * The ZestAuth SDK renders a countdown page at /logout and
   * then redirects back to /authentication.
   */
  goToLogoutUrl(): void {
    if (this.navigateFunc) {
      this.navigateFunc("/logout");
    } else {
      window.location.href = "/logout";
    }
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = this.tokenService.getToken();
    return !!token;
  }

  /**
   * Generate a random string of specified length
   * Used for generating unique identifiers
   */
  randomString(length: number): string {
    let result = "";
    const chars =
      "0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ";
    for (let i = length; i > 0; --i) {
      result += chars[Math.floor(Math.random() * chars.length)];
    }
    return result;
  }

  /**
   * Build the redirect URL after successful login
   * The URL contains the token in the hash fragment
   */
  getLoggedInRedirectUrl(token: Token): string {
    let url =
      APPURL +
      "/dashboard#access_token={access_token}&token_type={token_type}&expires_in={expires_in}";
    url = url.replace(
      "{access_token}",
      encodeURIComponent(token.access_token || ""),
    );
    url = url.replace(
      "{token_type}",
      encodeURIComponent(token.token_type || ""),
    );
    url = url.replace(
      "{expires_in}",
      encodeURIComponent(token.expires_in || ""),
    );
    return url;
  }

  /**
   * Get redirect URL with custom post-login redirect
   */
  getLoggedInRedirectUrlWithRoute(
    token: Token,
    postLoginRoute: string,
  ): string {
    let url = this.getLoggedInRedirectUrl(token);
    if (postLoginRoute) {
      url += `&post_login_redirect_url=${encodeURIComponent(postLoginRoute)}`;
    }
    return url;
  }
}

// Export singleton instance
const tokenService = new TokenService();
const loginService = new LoginService(tokenService);
export default loginService;
